import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import firebase from 'firebase/app';
import 'firebase/auth';
import { FUNCTIONS_BASE } from '../../config';
import Toolbar from './Toolbar';
import FloorPlanCanvas from './FloorPlanCanvas';
import PropertyPanel from './PropertyPanel';
import FloorSelector from './FloorSelector';

const FloorPlanEditor = ({ bizId }) => {
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentFloor, setCurrentFloor] = useState('ground');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const savedRef = useRef([]); // track last-saved state for reset

  const getAuthHeaders = async () => {
    const user = firebase.auth().currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadFloor = useCallback(async (floor) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${FUNCTIONS_BASE}/floorplan/${bizId}/${floor}`, { headers });
      const data = await res.json();
      const els = data.elements || [];
      setElements(els);
      savedRef.current = els;
      setDirty(false);
      setSelectedId(null);
    } catch (err) {
      console.error('Load floor plan error:', err);
      setSnack({ open: true, message: 'Failed to load floor plan', severity: 'error' });
    }
  }, [bizId]);

  useEffect(() => {
    if (bizId) loadFloor(currentFloor);
  }, [bizId, currentFloor, loadFloor]);

  const handleFloorChange = (floor) => {
    if (dirty && !window.confirm('You have unsaved changes. Switch floor anyway?')) return;
    setCurrentFloor(floor);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${FUNCTIONS_BASE}/floorplan/${bizId}/${currentFloor}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ elements, canvasWidth: 1200, canvasHeight: 800 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      savedRef.current = [...elements];
      setDirty(false);
      setSnack({ open: true, message: `Saved ${data.elementCount} elements`, severity: 'success' });
    } catch (err) {
      console.error('Save error:', err);
      setSnack({ open: true, message: err.message || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setElements([...savedRef.current]);
    setDirty(false);
    setSelectedId(null);
  };

  // Next table number = max existing + 1
  const nextTableNo = elements
    .filter((el) => el.type === 'table')
    .reduce((max, el) => Math.max(max, el.tableNo || 0), 0) + 1;

  const handleAdd = (newEl) => {
    setElements((prev) => [...prev, newEl]);
    setDirty(true);
    setSelectedId(newEl.id);
  };

  const handleChange = (updated) => {
    setElements((prev) => prev.map((el) => (el.id === updated.id ? updated : el)));
    setDirty(true);
  };

  const handleDelete = (id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    setSelectedId(null);
    setDirty(true);
  };

  const selectedElement = elements.find((el) => el.id === selectedId) || null;

  return (
    <Box role="main" aria-label="Floor plan editor" sx={{ display: 'flex', flexDirection: 'column', height: '85vh', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', background: '#fff' }}>
      <FloorSelector
        currentFloor={currentFloor}
        onFloorChange={handleFloorChange}
        onSave={handleSave}
        onReset={handleReset}
        saving={saving}
        dirty={dirty}
      />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Toolbar />
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 1 }}>
          <FloorPlanCanvas
            elements={elements}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onChange={handleChange}
            onAdd={handleAdd}
            nextTableNo={nextTableNo}
          />
        </Box>
        <PropertyPanel
          element={selectedElement}
          onChange={handleChange}
          onDelete={handleDelete}
        />
      </Box>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorPlanEditor;
